/**
 * The single radio-option primitive for every app (BL-1243, ratifying the
 * `RadioButton` row of the ADR 030 Canon).
 *
 * The merge collapses three implementations of the same job — admin-web's
 * `RadioButton`, bidder-web's `RadioButton` (the canonical base per ADR 030
 * § 3b: richer API, exported props interface), and admin-web's settings-v2
 * `Radio`. All three drew the identical DOM shape: a visually-hidden native
 * `<input type="radio">`, a 16px ring, a filled inner dot when selected, and
 * label content. They differed only in VALUES — ring weight, tap-target height,
 * hover treatment, where the brand color came from, and whether a secondary
 * "help" line sat under the label.
 *
 * WHY a `dialect` object rather than a pile of `className` props: ADR 030's
 * foundation is shared but its dialects are deliberately different (`--bw-*`
 * on the buyer site, settings-v2's `--text-*` on admin), and `sharing.md` § 1
 * caps a per-app skin at a 2–5 line wrapper. One frozen object of class names
 * per app keeps the appearance a piece of DATA the skin declares once, so the
 * wrapper stays a passthrough instead of re-deriving styling on every render.
 *
 * WHY the native input rather than a `role="radio"` div: native grouping by
 * `name` is what gives arrow-key navigation, form participation, and correct
 * assistive-tech announcement for free. The ring and dot are decoration drawn
 * beside it (`aria-hidden`), never a replacement for it.
 *
 * WHY colors arrive two different ways: static per-app colors belong in the
 * dialect's class names, where the `brandhex/no-hardcoded-brand-hex` lint can
 * see them. A tenant's brand color is only known at runtime (bidder resolves it
 * per auction house), so it arrives through `ringStyle`/`dotStyle` — the
 * sanctioned dynamic-value inline-style carve-out, and the only thing that
 * should ever use them.
 */
'use client';

import clsx from 'clsx';
import type { CSSProperties, ReactNode } from 'react';

/**
 * Per-app appearance binding — class names only, no structure.
 *
 * Every slot is optional: a consumer with no dialect gets the bare accessible
 * structure, which is the right default for a fourth app that hasn't declared
 * one yet.
 */
export interface RadioButtonDialect {
    /** The wrapping `<label>` — layout, gap, cursor, text treatment. */
    root?: string;
    /**
     * Classes on the native `<input type="radio">`. Omitted, the input is
     * `sr-only` — clipped to 1x1 and painted by the ring alone, which is
     * bidder's construction. A dialect that sets it owns the input's geometry
     * outright (nothing is appended), so admin can lay the real control over its
     * ring as a transparent layer and a pointer aimed at the control reaches it
     * instead of the drawn ring (BL-1957, BUG-2352).
     */
    input?: string;
    /**
     * Replaces `input` when `hideIndicator` is set: with no drawn ring there is no
     * control-sized spot to put the input on, so a dialect can stretch it over the
     * whole card (BUG-2352). Omitted, `input` applies either way.
     */
    inputFill?: string;
    /**
     * The 16px ring — classes that apply in BOTH states (size, shape, layout).
     *
     * Do NOT put a state-dependent property here. `ringUnchecked` and
     * `ringChecked` are mutually exclusive by construction; `ring` is not, so a
     * `border-color` left here would collide with the checked one at equal CSS
     * specificity and the later-emitted utility would win regardless of the
     * order they appear in the class attribute.
     */
    ring?: string;
    /** Applied to the ring only when UNSELECTED — e.g. the resting/hover border. */
    ringUnchecked?: string;
    /** Applied to the ring only when SELECTED — e.g. the brand-colored border. */
    ringChecked?: string;
    /** The filled inner dot. Rendered only when selected. */
    dot?: string;
    /** Wrapper around label + help. Lets a dialect stack them vertically. */
    content?: string;
    /** The primary label text. */
    label?: string;
    /** The secondary help text under the label. */
    help?: string;
}

/**
 * Props for {@link RadioButton}.
 */
export interface RadioButtonProps {
    /** Whether this option is selected. */
    checked: boolean;
    /** Called with this radio's `value` when it is selected. */
    onChange: (value: string) => void;
    /** The value this option represents. */
    value: string;
    /** Inline label content. */
    label: ReactNode;
    /** Optional secondary text rendered under the label. */
    help?: ReactNode;
    /** Radio group name — share across options for native single-select. */
    name?: string;
    /** Disables selection and blocks `onChange`. */
    disabled?: boolean;
    /** Extra classes on the wrapping label, merged after `dialect.root`. */
    className?: string;
    /** Inline style on the wrapping label — for a dynamic brand-tinted row. */
    style?: CSSProperties;
    /** Inline style on the ring — the sanctioned dynamic tenant-brand case. */
    ringStyle?: CSSProperties;
    /** Inline style on the inner dot — the sanctioned dynamic tenant-brand case. */
    dotStyle?: CSSProperties;
    /** Per-app appearance binding. See {@link RadioButtonDialect}. */
    dialect?: RadioButtonDialect;
    /**
     * Omit the drawn ring and dot. For a whole-card option whose own selected
     * skin carries the state (BUG-1993); the native input still owns the radio
     * semantics, so only the decoration goes.
     */
    hideIndicator?: boolean;
    /**
     * Test hook, rendered as `data-testid` on the wrapping `<label>` — NOT on
     * the input.
     *
     * The label is the one element that is clickable under every dialect. A
     * dialect with no `input` slot (bidder) keeps the real `<input>` `sr-only`
     * with the `aria-hidden` ring drawn over it, so a pointer aimed at the input
     * itself lands on the ring. Admin's dialects set `input` to a transparent
     * layer over the ring (BL-1957, BUG-2352), so there `getByRole('radio').check()` hits
     * the control directly and this handle is a convenience, not a workaround.
     * Clicking the label is still what a human does.
     */
    testId?: string;
}

/**
 * A single radio option with an inline label and an optional help line.
 * Compose several under one `name` to form a native single-select group.
 *
 * @param props - {@link RadioButtonProps}
 * @returns The rendered radio option.
 */
export function RadioButton({
    checked,
    onChange,
    value,
    label,
    help,
    name,
    disabled = false,
    className,
    style,
    ringStyle,
    dotStyle,
    dialect,
    hideIndicator = false,
    testId,
}: RadioButtonProps) {
    const hasContent = label != null || help != null;

    return (
        <label data-testid={testId} className={clsx(dialect?.root, className)} style={style}>
            {/* Invisible, but focusable and clickable — the group's real
                keyboard and form semantics live here, not on the drawn ring.
                `sr-only` unless the dialect owns the geometry (see `input`). */}
            <input
                type="radio"
                name={name}
                value={value}
                checked={checked}
                disabled={disabled}
                onChange={() => onChange(value)}
                className={(hideIndicator ? dialect?.inputFill : undefined) ?? dialect?.input ?? 'sr-only'}
            />
            {!hideIndicator && (
                <span
                    aria-hidden
                    className={clsx(
                        dialect?.ring,
                        // Exclusive, never additive — see RadioButtonDialect.ring.
                        checked ? dialect?.ringChecked : dialect?.ringUnchecked,
                    )}
                    style={ringStyle}
                >
                    {checked && <span className={dialect?.dot} style={dotStyle} />}
                </span>
            )}
            {hasContent && (
                <span className={dialect?.content}>
                    {label != null && <span className={dialect?.label}>{label}</span>}
                    {help != null && <span className={dialect?.help}>{help}</span>}
                </span>
            )}
        </label>
    );
}
